import React, { createContext, useContext, useState, useCallback } from 'react';

const SimulateurContext = createContext(null);

export const useSimulateur = () => {
  const context = useContext(SimulateurContext);
  if (!context) {
    throw new Error('useSimulateur must be used within SimulateurProvider');
  }
  return context;
};

const initialFormData = {
  // Step 1 - Transaction type
  transactionType: '', // 'vente' or 'location'
  
  // Step 2 - Property type
  propertyType: '', // 'appartement', 'maison', 'local'
  
  // Step 3 - Property details
  surface: '',
  constructionYear: '',
  hasGas: null, // true/false
  hasCellar: null,
  numberOfFloors: 1,
  
  // Step 4 - Diagnostics (auto-selected based on previous choices)
  selectedDiagnostics: [],
  
  // Step 5 - Client info
  clientName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postalCode: '',
  preferredDate: '',
  message: '',
};

export const SimulateurProvider = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [calculatedQuote, setCalculatedQuote] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const totalSteps = 6;

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const updateMultipleFields = useCallback((updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  const goToNextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const goToPrevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  }, [totalSteps]);

  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setFormData(initialFormData);
    setCalculatedQuote(null);
    setIsSubmitting(false);
    setIsSubmitted(false);
  }, []);

  const value = {
    currentStep,
    totalSteps,
    formData,
    calculatedQuote,
    isSubmitting,
    isSubmitted,
    setCurrentStep,
    setFormData,
    updateFormData,
    updateMultipleFields,
    goToNextStep,
    goToPrevStep,
    goToStep,
    setCalculatedQuote,
    setIsSubmitting,
    setIsSubmitted,
    resetForm,
  };

  return (
    <SimulateurContext.Provider value={value}>
      {children}
    </SimulateurContext.Provider>
  );
};
